import type { ItemPedido, Pedido, SubPedido } from '../db/tipos'

// Aba Historico do cliente (print dela, 11/10). Uma linha por FORNECEDORA do
// pedido (o sub-pedido, numero 2026-0042-A): e o que tem status proprio.
// Cancelada aparece na lista, mas fica fora dos totais, do grafico e do top 5
// -- mesma regra da previa/PDF ("nao cobrado").

export interface FiltroHistorico {
  representadaId: string | null
  de: string // AAAA-MM-DD, dia local
  ate: string
  busca: string
}

export interface LinhaHistorico {
  id: string
  pedidoId: string
  data: string
  numero: string
  fornecedora: string
  itens: number
  total: number
  status: SubPedido['status']
}

export interface Historico {
  linhas: LinhaHistorico[]
  // mediaItens: itens (linhas de produto) por pedido no periodo -- ela, 11/10.
  totais: { valor: number; pedidos: number; mediaItens: number }
  // Um mes por barra, do primeiro ao ultimo mes do PERIODO filtrado -- o mesmo
  // periodo dos totais (decisao dela 11/10: antes era jan-dez de um ano so, e o
  // grafico desencontrava do total num periodo entre dois anos).
  meses: { chave: string; rotulo: string; valor: number }[]
  rotuloPeriodo: string
  topProdutos: { nome: string; valor: number; fatia: number }[]
  // Valor dos produtos FORA do top 5: vira a fatia cinza "Outros" da rosca,
  // para o desenho bater com a % da legenda (que e sobre o total).
  outros: number
}

const NOMES_MES = ['Jan', 'Fev', 'Mar', 'Abr', 'Mai', 'Jun', 'Jul', 'Ago', 'Set', 'Out', 'Nov', 'Dez']

// Os meses entre dois dias 'AAAA-MM-DD', inclusive, rotulados 'Out/25'.
export function mesesDoPeriodo(de: string, ate: string): { chave: string; rotulo: string; valor: number }[] {
  const meses: { chave: string; rotulo: string; valor: number }[] = []
  let a = Number(de.slice(0, 4)), m = Number(de.slice(5, 7))
  const aFim = Number(ate.slice(0, 4)), mFim = Number(ate.slice(5, 7))
  while (a < aFim || (a === aFim && m <= mFim)) {
    meses.push({ chave: `${a}-${String(m).padStart(2, '0')}`, rotulo: `${NOMES_MES[m - 1]}/${String(a).slice(2)}`, valor: 0 })
    if (++m > 12) { m = 1; a++ }
  }
  return meses
}

// Periodo padrao do Historico (decisao dela 11/10): os ultimos 12 meses, do
// dia 1 de 11 meses atras ate o fim do mes atual. Dia LOCAL, sem Date->ISO
// (licao do fuso UTC).
export function ultimos12Meses(hoje: Date): { de: string; ate: string } {
  const ini = new Date(hoje.getFullYear(), hoje.getMonth() - 11, 1)
  const fim = new Date(hoje.getFullYear(), hoje.getMonth() + 1, 0)
  const dia = (d: Date) => `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`
  return { de: dia(ini), ate: dia(fim) }
}

function chave(texto: string): string {
  return texto.normalize('NFD').replace(/[̀-ͯ]/g, '').trim().toLowerCase()
}
const arred2 = (n: number) => Math.round(n * 100) / 100

export function montarHistorico({
  clienteId, pedidos, subPedidos, itens, produtos, representadas, filtro,
}: {
  clienteId: string
  pedidos: readonly Pedido[]
  subPedidos: readonly SubPedido[]
  itens: readonly ItemPedido[]
  produtos: ReadonlyMap<string, string>
  representadas: ReadonlyMap<string, string>
  filtro: FiltroHistorico
}): Historico {
  const busca = chave(filtro.busca)
  const doPedido = new Map(
    pedidos
      .filter((p) => p.cliente_id === clienteId && p.apagado_em === null && p.status !== 'aberto'
        && p.data >= filtro.de && p.data <= filtro.ate)
      .map((p) => [p.id, p]),
  )
  const itensDoSub = new Map<string, ItemPedido[]>()
  for (const i of itens) {
    if (i.apagado_em !== null) continue
    // push, e nao [...lista, i]: copiar a lista a cada item era O(n²) (code review 11/10, ponto 10).
    const lista = itensDoSub.get(i.sub_pedido_id)
    if (lista) lista.push(i)
    else itensDoSub.set(i.sub_pedido_id, [i])
  }

  const escolhidos = subPedidos.filter((s) => {
    const p = doPedido.get(s.pedido_id)
    if (!p || s.apagado_em !== null || s.status === 'rascunho') return false
    if (filtro.representadaId && s.representada_id !== filtro.representadaId) return false
    if (!busca) return true
    if (chave(`${p.numero}-${s.letra}`).includes(busca)) return true
    return (itensDoSub.get(s.id) ?? []).some((i) => chave(produtos.get(i.produto_id) ?? '').includes(busca))
  })

  const linhas: LinhaHistorico[] = escolhidos
    .map((s) => {
      const p = doPedido.get(s.pedido_id)!
      return {
        id: s.id, pedidoId: p.id, data: p.data, numero: `${p.numero}-${s.letra}`,
        fornecedora: representadas.get(s.representada_id) ?? '—',
        // Quantos ITENS o pedido tem (linhas de produto), nao a soma das
        // quantidades vendidas -- correcao dela, 11/10.
        itens: (itensDoSub.get(s.id) ?? []).length,
        total: s.valor_total, status: s.status,
      }
    })
    .sort((a, b) => b.data.localeCompare(a.data) || a.numero.localeCompare(b.numero))

  const validas = linhas.filter((l) => l.status !== 'cancelado')
  const meses = mesesDoPeriodo(filtro.de, filtro.ate)
  const indice = new Map(meses.map((m, i) => [m.chave, i]))
  for (const l of validas) {
    const i = indice.get(l.data.slice(0, 7))
    if (i !== undefined) meses[i].valor += l.total
  }

  const porProduto = new Map<string, number>()
  for (const l of validas) {
    for (const i of itensDoSub.get(l.id) ?? []) {
      const nome = produtos.get(i.produto_id) ?? '—'
      porProduto.set(nome, (porProduto.get(nome) ?? 0) + i.total_item)
    }
  }
  const totalProdutos = [...porProduto.values()].reduce((a, b) => a + b, 0)
  const topProdutos = [...porProduto.entries()]
    .sort((a, b) => b[1] - a[1])
    .slice(0, 5)
    .map(([nome, valor]) => ({ nome, valor: arred2(valor), fatia: totalProdutos ? arred2(valor / totalProdutos) : 0 }))

  const pedidosValidos = new Set(validas.map((l) => l.pedidoId)).size
  const itensValidos = validas.reduce((soma, l) => soma + l.itens, 0)
  return {
    linhas,
    totais: {
      valor: arred2(validas.reduce((soma, l) => soma + l.total, 0)),
      pedidos: pedidosValidos,
      mediaItens: pedidosValidos ? Math.round((itensValidos / pedidosValidos) * 10) / 10 : 0,
    },
    meses: meses.map((m) => ({ ...m, valor: arred2(m.valor) })),
    rotuloPeriodo: meses.length ? `${meses[0].rotulo} – ${meses[meses.length - 1].rotulo}` : '',
    topProdutos,
    outros: arred2(totalProdutos - topProdutos.reduce((s, p) => s + p.valor, 0)),
  }
}
